import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/Microgrid/BreadCrumbsFile";
import "./SimulationResults.css";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
  MdOutlineFileDownload,
} from "react-icons/md";
import {
  fetchResultsData,
  downloadResultsData,
} from "../FileUpload/ResultsDisplay";
import { simulationService } from "../../../../services/api/simulationService";
import { resultsParserWorkerUrl } from "../../../../services/workerService";
import { useNavigation } from "../../../../contexts/NavigationContext";
import DeleteSimulationModal from "./DeleteSimulationModal";
import EditSimulationNameModal from "./EditSimulationNameModal";
import {
  runTimeBucketKey,
  runTimeBucketLabel,
} from "../../utils/runTimeBuckets";
import {
  groupMeterRowsIntoRuns,
  runMetersLabel,
} from "../../utils/simulationRuns";
import useToday from "../../hooks/useToday";
import useCollapsedGroups from "../../hooks/useCollapsedGroups";

// Components
import Button from "../../../../components/Common/Button";
import Table, {
  KebabMenu,
  TableButton,
} from "../../../../components/Common/Table";

const ROWS_PER_PAGE = 500;

// Formats the simulation *period*, whose start/end are date-only strings
// ("2025-01-01") parsed out of the results filename. `new Date()` reads a
// date-only ISO string as UTC midnight, so without an explicit timeZone the
// formatter would re-render that instant in the viewer's zone and shift the
// label a day earlier anywhere west of UTC. These are calendar dates, not
// instants, so pin the formatter to UTC to echo the string as written.
const LONG_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/* Date and time are formatted separately and joined with " at". One combined
 * formatter's separators depend on the ICU version — "May 11, 2026, 9:00 PM"
 * in some, "May 11, 2026 at 9:00 PM" in others — so patching its first comma
 * gave "May 11 at 2026, 9:00 PM". */
const GENERATED_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "short",
  day: "numeric",
});

const GENERATED_TIME_FORMATTER = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const formatLongDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return LONG_DATE_FORMATTER.format(d);
};

// Renders the results file's generation time. Unlike the period above this is
// a true instant (blob `created`, ISO 8601 with an offset), so local time is
// the correct rendering and no timeZone override is applied.
const formatGeneratedAt = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return `${GENERATED_DATE_FORMATTER.format(d)} at ${GENERATED_TIME_FORMATTER.format(d)}`;
};

const SimulationResults = () => {
  const { containerName } = useParams();
  const userEmail = sessionStorage.getItem("selectedCustomerEmail");
  const customerPrefix = userEmail?.replace(/[@.]/g, "-") || "";
  const [simulations, setSimulations] = useState([]);
  const name = sessionStorage.getItem("microgridDesign");
  const meterName = sessionStorage.getItem("MicrogridmeterName");
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const storageAccountName = sessionStorage.getItem("storageAccountName");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);
  const [previewData, setPreviewData] = useState({});
  const [visibleSeriesKey, setVisibleSeriesKey] = useState(null);
  const [visibleDailyserieskey, setVisibleDailySerieskey] = useState(null);
  const { setSelectedSimulation, setTimeSeriesFileName } = useNavigation();
  const location = useLocation();
  const [refreshList, setRefreshList] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [savingName, setSavingName] = useState(false);
  const jobTitle = sessionStorage.getItem("jobTitle");

  const isCustomer = jobTitle === "Customer";
  // The start of the viewer's local today. It changes only at local midnight,
  // so the groups stay put all day and "Today" becomes "Yesterday" overnight.
  const today = useToday();
  // Collapsed groups live here and in sessionStorage, not in the Table, which
  // a refetch's spinner or a return from Generate Simulation unmounts.
  const [collapsedGroupKeys, setGroupCollapsed] = useCollapsedGroups(
    `${storageAccountName}/${name}`,
  );

  /* CSV parsing runs in a Web Worker. The worker used to be constructed in the
   * component body, which spawned a fresh thread on every render and only ever
   * terminated the one captured by the handler that happened to run — every
   * other instance leaked. Workers are now created per parse, by the handler
   * that needs one, and terminated as soon as that parse settles. The ref
   * tracks the ones still in flight so unmounting mid-parse cleans them up. */
  const activeWorkersRef = useRef(new Set());

  const spawnParserWorker = useCallback(() => {
    /* URL comes from workerService so the cache-busting version lives in one
     * place with the other CSV worker's, rather than as a bare literal here
     * that nobody would think to bump. */
    const instance = new Worker(resultsParserWorkerUrl());
    activeWorkersRef.current.add(instance);
    return instance;
  }, []);

  const releaseParserWorker = useCallback((instance) => {
    if (!instance) return;
    activeWorkersRef.current.delete(instance);
    instance.terminate();
  }, []);

  useEffect(() => {
    const inFlight = activeWorkersRef.current;
    return () => {
      inFlight.forEach((instance) => instance.terminate());
      inFlight.clear();
    };
  }, []);

  useEffect(() => {
    if (!storageAccountName || !name) {
      toast.error("No microgrid selected. Please choose a microgrid first.");
      navigate("/microgrid-page-design");
    }
  }, [storageAccountName, name, navigate]);

  useEffect(() => {
    if (location.state?.refresh) {
      setRefreshList((prev) => prev + 1);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const accessToken = sessionStorage.getItem("accessToken");
        if (!accessToken) {
          throw new Error(
            "No accessToken found in session storage! Please log in.",
          );
        }

        const data = await simulationService.getList({ customerName: storageAccountName, accessToken });

        const allResults = getAllResultsFromResponse(data);
        const files = extractSimulationsInfo(allResults);
        files.sort((a, b) => new Date(b.created) - new Date(a.created));

        setSimulations(groupMeterRowsIntoRuns(files));
      } catch (err) {
        console.error("[MicrogridDesigns] Error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (storageAccountName) {
      fetchData();
    }
  }, [baseUrl, storageAccountName, customerPrefix, refreshList]);

  const handleDownloadHistory = async (fileObj) => {
    if (!fileObj) return;
    try {
      const token = sessionStorage.getItem("accessToken");
      const type = getFileType(fileObj);
      const microgridName = sessionStorage.getItem("microgridDesign");
      await downloadResultsData(
        type,
        fileObj,
        baseUrl,
        storageAccountName,
        microgridName,
        token,
      );
    } catch (err) {
      console.error("Error downloading file:", err);
      toast.error(`Error downloading ${fileObj.name}: ${err.message}`);
    }
  };

  const handleViewTimeseries = async (fileName, timeKey) => {
    if (!fileName) return;

    try {
      const token = sessionStorage.getItem("accessToken");
      const microgridName = sessionStorage.getItem("microgridDesign");

      const csvText = await fetchResultsData(
        "Timeseries",
        fileName,
        baseUrl,
        storageAccountName,
        microgridName,
        token,
        "handleViewDailySummary",
      );

      // Created only once the CSV has actually arrived, so a failed fetch
      // never leaves a thread behind for the outer catch to clean up.
      const worker = spawnParserWorker();

      worker.postMessage({ csvText, taskId: fileName });

      worker.onmessage = (e) => {
        try {
          const { success, rawData, headers, error } = e.data;

          if (!success) {
            alert(`Error parsing ${fileName}: ${error}`);
            releaseParserWorker(worker);
            return;
          }

          const rows = Array.isArray(rawData) ? rawData : [];

          setPreviewData((prev) => {
            const current = prev[timeKey] || {};
            return {
              ...prev,
              [timeKey]: {
                ...current,
                timeseries: rows,
                headers,
                page: 0,
                totalPages: Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE)),
              },
            };
          });

          setVisibleSeriesKey((prevKey) =>
            prevKey === timeKey ? null : timeKey,
          );
        } catch (err) {
          console.error("Worker parsing error:", err);
          alert(`Error processing ${fileName}: ${err.message}`);
        } finally {
          releaseParserWorker(worker);
        }
      };

      worker.onerror = (err) => {
        console.error("Worker runtime error:", err);
        alert(`Worker error in ${fileName}`);
        releaseParserWorker(worker);
      };
    } catch (err) {
      console.error("handleViewTimeseries error:", err);
      alert(`Error viewing ${fileName}: ${err.message}`);
    }
  };

  const handleViewDailySummary = async (fileObj, timeKey) => {
    if (!fileObj) return;
    try {
      const token = sessionStorage.getItem("accessToken");
      const microgridName = sessionStorage.getItem("microgridDesign");
      const csvText = await fetchResultsData(
        "Daily Summary",
        fileObj,
        baseUrl,
        storageAccountName,
        microgridName,
        token,
        "handleViewDailySummary",
      );

      // Created only once the CSV has actually arrived, so a failed fetch
      // never leaves a thread behind for the outer catch to clean up.
      const worker = spawnParserWorker();

      worker.postMessage({ csvText, taskId: fileObj });

      worker.onmessage = (e) => {
        try {
          const { success, data, rawData, error } = e.data;

          if (!success) {
            alert(`Error parsing ${fileObj}: ${error}`);
            releaseParserWorker(worker);
            return;
          }

          const rows = Array.isArray(rawData) ? rawData : [];

          setPreviewData((prev) => {
            const current = prev[timeKey] || {};
            return {
              ...prev,
              [timeKey]: {
                ...current,
                dailySummary: rows,
                page: 0,
                totalPages: Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE)),
              },
            };
          });

          setVisibleDailySerieskey((prevKey) =>
            prevKey === timeKey ? null : timeKey,
          );
        } catch (err) {
          console.error("Worker parsing error:", err);
          alert(`Error processing ${fileObj}: ${err.message}`);
        } finally {
          releaseParserWorker(worker);
        }
      };

      worker.onerror = (err) => {
        console.error("Worker runtime error:", err);
        alert(`Worker error in ${fileObj}`);
        releaseParserWorker(worker);
      };
    } catch (err) {
      console.error("handleViewDailySummarys error:", err);
      alert(`Error viewing ${fileObj}: ${err.message}`);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    if (!pendingDelete.dateTimeStamp) {
      toast.error(
        "Cannot delete: this simulation row is missing a date_time_stamp.",
      );
      return;
    }
    try {
      setDeleting(true);
      await simulationService.deleteSimulation({
        storageAccount: storageAccountName,
        microgridName: name,
        dateTimeStamp: pendingDelete.dateTimeStamp,
      });
      toast.success(
        `Deleted "${pendingDelete.simulationName || runMetersLabel(pendingDelete)}"`,
      );
      setPendingDelete(null);
      setRefreshList((prev) => prev + 1);
    } catch (err) {
      console.error("[SimulationResults] Delete failed:", err);
      const backendMsg = err?.response?.data?.message || err.message;
      toast.error(`Failed to delete simulation: ${backendMsg}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveName = async (newName) => {
    if (!pendingEdit) return;
    try {
      setSavingName(true);
      const data = await simulationService.updateSimulationName({
        storageAccount: storageAccountName,
        microgridName: name,
        dateTimeStamp: pendingEdit.dateTimeStamp,
        simulationName: newName,
      });
      const echoed = data?.simulation_name ?? "";
      setSimulations((prev) =>
        prev.map((s) =>
          s.dateTimeStamp === pendingEdit.dateTimeStamp
            ? { ...s, simulationName: echoed.length > 0 ? echoed : null }
            : s,
        ),
      );
      const displayName = echoed || newName;
      if (Array.isArray(data?.warnings) && data.warnings.length > 0) {
        toast.warn(
          `Saved, but ${data.warnings.length} blob(s) were skipped.`,
        );
        // A partial save means the optimistic row above may disagree with what
        // was actually stored, and the name is re-derived from the blobs on the
        // next mount — so the row would silently revert. Refetch to reconcile.
        // The clean-save path deliberately does not, since the response echoes
        // the stored name and a refetch there would discard the optimistic row.
        setRefreshList((prev) => prev + 1);
      } else if (newName === "") {
        toast.success("Simulation name cleared.");
      } else {
        toast.success(
          displayName
            ? `Updated to "${displayName}".`
            : "Simulation name updated.",
        );
      }
      setPendingEdit(null);
      /* Deliberately no setRefreshList() here. `refreshList` is a dependency of
       * the list-fetch effect, so bumping it flipped `loading` back on, swapped
       * the table for a spinner and then overwrote the row with the pre-rename
       * server copy — discarding the optimistic write a few lines above. A
       * rename only changes `simulation_name`, and the response echoes the
       * stored value, so the optimistic update is already authoritative and
       * nothing else in the row can have changed. Delete still refreshes,
       * because there a whole row disappears. */
    } catch (err) {
      const msg =
        err?.response?.data?.message || err.message || "Unknown error";
      const status = err?.response?.status;
      if (status === 404 || /No timeseries CSV blobs matching/i.test(msg)) {
        toast.error("This run no longer exists or has no timeseries data.");
      } else {
        toast.error(`Failed to update simulation name: ${msg}`);
      }
    } finally {
      setSavingName(false);
    }
  };

  const getAllResultsFromResponse = (jsonData) => {
    const selectedCustomerEmail =
      sessionStorage.getItem("selectedCustomerEmail") ||
      sessionStorage.getItem("email");
    if (!selectedCustomerEmail) return [];
    const formattedEmail = name;
    if (jsonData[formattedEmail] && jsonData[formattedEmail].results) {
      return jsonData[formattedEmail].results;
    }
    return [];
  };

  const getFileType = (fileName) => {
    if (fileName.startsWith("timeseries_")) return "Timeseries";
    return "Daily Summary";
  };

  const getMeterName = (n) => {
    const match = n.match(/^([^-\s]+)/);
    return match ? match[1] : null;
  };

  const extractSimulationsInfo = (files) => {
    const results = [];

    const meterFiles = files.filter(
      (f) =>
        f.name.toLowerCase().startsWith("meter") ||
        f.name.includes("timeseries"),
    );
    const dailyFiles = files.filter((f) =>
      f.name.toLowerCase().startsWith("daily-summary"),
    );

    meterFiles.forEach((meter) => {
      const { name: fileName, created } = meter;

      const match = fileName.match(
        /-(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})-timeseries/i,
      );
      if (!match) return;

      const [_, startDate, endDate] = match;

      // Full MMDDYY_HHMM stamp identifying this simulation run, used by
      // the backend's delete_simulation_results endpoint to atomically
      // remove all blobs (results + versioned sysconfig) for the run.
      const stampMatch = fileName.match(/-(\d{6}_\d{4})\.csv$/);
      const dateTimeStamp = stampMatch ? stampMatch[1] : null;

      const relevantDailyFiles = dailyFiles.filter((f) =>
        f.name.includes(`${startDate}_${endDate}`),
      );

      let dailySummaryName = null;
      let dailySummaryCode = null;

      if (relevantDailyFiles.length) {
        const meterTime = new Date(created).getTime();
        let closest = relevantDailyFiles[0];
        let minDiff = Math.abs(
          new Date(relevantDailyFiles[0].created).getTime() - meterTime,
        );

        relevantDailyFiles.forEach((df) => {
          const diff = Math.abs(new Date(df.created).getTime() - meterTime);
          if (diff < minDiff) {
            closest = df;
            minDiff = diff;
          }
        });

        dailySummaryName = closest.name;
        const codeMatch = closest.name.match(/_(\d{4})\.csv$/);
        dailySummaryCode = codeMatch ? codeMatch[1] : null;
      }

      const customName =
        typeof meter.simulation_name === "string" &&
        meter.simulation_name.length > 0
          ? meter.simulation_name
          : null;

      results.push({
        fileName,
        meterName: getMeterName(fileName),
        simulationName: customName,
        startDate,
        endDate,
        created,
        dailySummary: dailySummaryName,
        dailySummaryCode,
        dateTimeStamp,
      });
    });

    return results;
  };

  const handleViewGraphs = (sim) => {
    setSelectedSimulation({
      name: sim.meterName,
      startDate: sim.startDate,
      endDate: sim.endDate,
    });

    sessionStorage.setItem("timeSeriesFileName", sim.fileName);
    sessionStorage.setItem("dailySummaryFileName", sim.dailySummary);
    sessionStorage.setItem(
      "startDateAndEndDate",
      sim.startDate + "-" + sim.endDate,
    );
    sessionStorage.setItem("simulationDisplayName", sim.simulationName ?? "");
    setTimeSeriesFileName(sim.fileName);
    navigate("/summary");
  };

  const toggleRow = (timeKey) => {
    setExpandedRow((prev) => (prev === timeKey ? null : timeKey));
  };

  // Row key must be unique per simulation run. A row is one run, and
  // `sim.fileName` is its first meter's timeseries CSV, which belongs to that
  // run alone; `dailySummaryCode` is only a 4-digit suffix of the matched
  // daily-summary file and can collide when two runs share a daily summary.
  const getRowKey = (sim) => sim.fileName;

  // Renders the results panel (each meter's Time Series + the run's Daily
  // Summary + previews) shown when a simulation row is expanded.
  const renderResultsPanel = (sim) => {
    const timeKey = getRowKey(sim);
    const preview = previewData[timeKey];
    const isMultiMeter = sim.meters.length > 1;

    return (
      <div className="section" onClick={(e) => e.stopPropagation()}>
        <h4>Results</h4>

        {/* Timeseries Section: one line per meter. The preview is keyed by the
            meter's file, which for a single-meter run is the row key, as
            before. */}
        {sim.meters.map((meter) => {
          const seriesKey = meter.fileName;
          const seriesPreview = previewData[seriesKey];
          return (
            <React.Fragment key={seriesKey}>
              <div className="file-row">
                <span className="file-label">
                  {isMultiMeter ? `Time Series (${meter.meterName}):` : "Time Series:"}
                </span>
                <span className="file-name">{meter.fileName}</span>
                <button
                  type="button"
                  className="view-link"
                  aria-label={
                    isMultiMeter
                      ? `${visibleSeriesKey === seriesKey ? "Hide" : "View"} time series (${meter.meterName})`
                      : undefined
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewTimeseries(meter.fileName, seriesKey);
                  }}
                >
                  {visibleSeriesKey === seriesKey ? "Hide" : "View"}
                </button>
                <TableButton
                  variant="icon"
                  className="download-icon-button"
                  aria-label={
                    isMultiMeter
                      ? `Download time series (${meter.meterName})`
                      : "Download time series"
                  }
                  title="Download"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownloadHistory(meter.fileName);
                  }}
                >
                  <MdOutlineFileDownload />
                </TableButton>
                {isMultiMeter && (
                  <button
                    type="button"
                    className="view-link"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewGraphs({
                        ...sim,
                        fileName: meter.fileName,
                        meterName: meter.meterName,
                      });
                    }}
                  >
                    Open summary
                  </button>
                )}
              </div>

              {visibleSeriesKey === seriesKey &&
                seriesPreview?.timeseries?.length > 0 && (
                  <div className="history-preview">
                    <table className="history-table">
                      <thead>
                        {seriesPreview.headers.map((headerRow, idx) => (
                          <tr key={idx}>
                            {headerRow.map((header, i) => (
                              <th key={i}>{header}</th>
                            ))}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {seriesPreview.timeseries
                          .slice(seriesPreview.headers.length)
                          .slice(
                            seriesPreview.page * ROWS_PER_PAGE,
                            (seriesPreview.page + 1) * ROWS_PER_PAGE,
                          )
                          .map((row, rowIndex) => (
                            <tr key={rowIndex}>
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>

                    <div style={{ marginTop: "10px" }}>
                      <button
                        disabled={seriesPreview.page === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewData((prev) => ({
                            ...prev,
                            [seriesKey]: {
                              ...prev[seriesKey],
                              page: prev[seriesKey].page - 1,
                            },
                          }));
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ margin: "0 10px" }}>
                        Page {seriesPreview.page + 1} of {seriesPreview.totalPages}
                      </span>
                      <button
                        disabled={seriesPreview.page + 1 === seriesPreview.totalPages}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewData((prev) => ({
                            ...prev,
                            [seriesKey]: {
                              ...prev[seriesKey],
                              page: prev[seriesKey].page + 1,
                            },
                          }));
                        }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
            </React.Fragment>
          );
        })}

        {/* Daily Summary Section */}
        <div className="file-row">
          <span className="file-label">Daily Summary:</span>
          <span className="file-name">{sim.dailySummary}</span>
          <button
            type="button"
            className="view-link"
            onClick={(e) => {
              e.stopPropagation();
              handleViewDailySummary(sim.dailySummary, timeKey);
            }}
          >
            {visibleDailyserieskey === timeKey ? "Hide" : "View"}
          </button>
          <TableButton
            variant="icon"
            className="download-icon-button"
            aria-label="Download daily summary"
            title="Download"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadHistory(sim.dailySummary);
            }}
          >
            <MdOutlineFileDownload />
          </TableButton>
        </div>

        {visibleDailyserieskey === timeKey &&
          preview?.dailySummary?.length > 0 && (
            <div className="history-preview">
              <table className="history-table">
                <thead>
                  {preview.headers?.map((headerRow, idx) => (
                    <tr key={idx}>
                      {headerRow.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {preview.dailySummary
                    .slice(preview.headers?.length || 0)
                    .slice(
                      preview.page * ROWS_PER_PAGE,
                      (preview.page + 1) * ROWS_PER_PAGE,
                    )
                    .map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                </tbody>
              </table>

              <div style={{ marginTop: "10px" }}>
                <button
                  disabled={preview.page === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewData((prev) => ({
                      ...prev,
                      [timeKey]: {
                        ...prev[timeKey],
                        page: prev[timeKey].page - 1,
                      },
                    }));
                  }}
                >
                  Previous
                </button>
                <span style={{ margin: "0 10px" }}>
                  Page {preview.page + 1} of {preview.totalPages}
                </span>
                <button
                  disabled={preview.page + 1 === preview.totalPages}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewData((prev) => ({
                      ...prev,
                      [timeKey]: {
                        ...prev[timeKey],
                        page: prev[timeKey].page + 1,
                      },
                    }));
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
      </div>
    );
  };

  const columns = useMemo(
    () => [
      {
        key: "meter_name",
        header: "Simulation Name",
        wrap: true,
        render: (sim) => {
          const display = sim.simulationName ?? "\u2014";
          return (
            <>
              <div
                title={display}
                style={{
                  fontWeight: 600,
                  fontSize: "14px",
                  color: "#1e293b",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {display}
              </div>
              <div
                style={{
                  fontSize: "12px",
                  color: "#64748b",
                  marginTop: "2px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {runMetersLabel(sim)}
                {sim.created
                  ? ` \u2013 Generated ${formatGeneratedAt(sim.created)}`
                  : ""}
              </div>
            </>
          );
        },
      },
      {
        key: "period",
        header: "Period",
        width: "260px",
        hideBelow: "xl",
        render: (sim) => {
          const start = formatLongDate(sim.startDate);
          const end = formatLongDate(sim.endDate);
          if (!start && !end) {
            return <span style={{ color: "#94a3b8" }}>&mdash;</span>;
          }
          return (
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {start} &ndash; {end}
            </span>
          );
        },
      },
      {
        key: "actions",
        header: "Actions",
        align: "right",
        width: "110px",
        render: (sim) => {
          const timeKey = getRowKey(sim);
          const isExpanded = expandedRow === timeKey;
          return (
            <div className="wg-table__actions">
              <TableButton
                variant="icon"
                aria-label={isExpanded ? "Collapse row" : "Expand row"}
                title={isExpanded ? "Collapse" : "Expand"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRow(timeKey);
                }}
              >
                {isExpanded ? (
                  <MdOutlineKeyboardArrowUp />
                ) : (
                  <MdOutlineKeyboardArrowDown />
                )}
              </TableButton>
              <KebabMenu
                items={[
                  {
                    label: "Edit name",
                    onClick: () => setPendingEdit(sim),
                    disabled: !sim.dateTimeStamp,
                  },
                  {
                    label: "Delete",
                    onClick: () => setPendingDelete(sim),
                    disabled: !sim.dateTimeStamp,
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    [expandedRow, visibleSeriesKey, visibleDailyserieskey, previewData],
  );

  if (!name || !storageAccountName) {
    return (
      <div className="simulation-page">
        <div className="spinner-container-sim">
          <div className="spinner-sim"></div>
        </div>
      </div>
    );
  }

  const breadcrumbCrumbs = isCustomer
    ? [
        {
          label: sessionStorage.getItem("customerFirstName"),
          path: `/microgrid-page-design`,
        },
        {
          label: name.replace(customerPrefix + "-", "") + "-" + "Simulations",
        },
      ]
    : sessionStorage.getItem("jobTitle") === "Employee"
      ? [
          {
            label: "Customer List",
            path: "/employeehomepage",
          },
          {
            label: sessionStorage.getItem("customerFirstName"),
            path: `/microgrid-page-design`,
          },
          {
            label: name.replace(customerPrefix + "-", "") + "-" + "Simulations",
          },
        ]
      : [
          {
            label: "Customer List",
            path: "/employees",
            state: { activeTab: "customers" },
          },
          {
            label: sessionStorage.getItem("customerFirstName"),
            path: `/microgrid-page-design`,
          },
          {
            label: name.replace(customerPrefix + "-", "") + "-" + "Simulations",
          },
        ];

  const toolbar = {
    left: <h2 className="wg-page-header">Simulations</h2>,
    right:
      jobTitle !== "Customer" ? (
        <Button
          variant="primary"
          size="md"
          onClick={() => navigate("/generate-simulation")}
        >
          + Generate Simulation
        </Button>
      ) : null,
  };

  return (
    <div className="simulation-page">
      <Breadcrumbs crumbs={breadcrumbCrumbs} />

      <div className="employee-table-container">
        {loading ? (
          <div className="spinner-container-sim">
            <div className="spinner-sim"></div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={simulations}
            rowKey={getRowKey}
            hoverable
            emptyState="No Simulations available"
            toolbar={toolbar}
            onRowClick={(sim) => handleViewGraphs(sim)}
            expandedRowKey={expandedRow}
            renderExpandedRow={(sim) => renderResultsPanel(sim)}
            getGroupKey={(sim) => runTimeBucketKey(sim.created, today)}
            renderGroupHeader={(key) => runTimeBucketLabel(key, today)}
            groupsCollapsible
            collapsedGroupKeys={collapsedGroupKeys}
            onGroupCollapsedChange={setGroupCollapsed}
          />
        )}
      </div>

      <DeleteSimulationModal
        open={!!pendingDelete}
        simulation={pendingDelete}
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />

      <EditSimulationNameModal
        open={!!pendingEdit}
        simulation={pendingEdit}
        loading={savingName}
        onSave={handleSaveName}
        onClose={() => {
          if (!savingName) setPendingEdit(null);
        }}
      />
    </div>
  );
};

export default SimulationResults;
